import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { BaseRequestListComponent } from '../base-request-list/base-request-list.component';
import { CommonGridComponent, GridColumn, GridAction } from '../../../shared/components/common-grid/common-grid.component';
import { RequestManagementService, PaginationInfo } from '../../../shared/services/request-management.service';

@Component({
  selector: 'app-inbox',
  standalone: true,
  imports: [CommonModule, TranslateModule, CommonGridComponent],
  templateUrl: './inbox.component.html',
  styleUrls: ['./inbox.component.scss']
})
export class InboxComponent extends BaseRequestListComponent implements OnInit {
  
  /**
   * Define columns for inbox grid
   */
  columns: GridColumn[] = [
    { key: 'number', label: 'Request Number', translationKey: 'requests.number' },
    {
      key: 'module',
      label: 'Module',
      translationKey: 'requests.module',
      type: 'custom',
      customRender: (value: any) => this.translate.instant(`workflow_types.${value}`)
    },
    { key: 'taskReceivedDate', label: 'Received Date', translationKey: 'requests.receivedDate', type: 'date' },
    { key: 'modifiedDate', label: 'Update Date', translationKey: 'requests.updateDate', type: 'date' },
    { key: 'sentFrom', label: 'From', translationKey: 'requests.from' },
    { key: 'lockedByUser', label: 'Locked By', translationKey: 'requests.lockedBy' },
    { key: 'comment', label: 'Notes', translationKey: 'requests.notes' },
    {
      key: 'status',
      label: 'Status',
      translationKey: 'requests.status',
      type: 'badge'
    }
  ];

  /**
   * Define actions available for inbox requests
   */
  actions: GridAction[] = [
    { 
      id: 'edit', 
      label: 'common.edit', 
      type: 'edit', 
      icon: 'bi-pencil'
    }
  ];

  constructor(
    private requestService: RequestManagementService,
    private router: Router,
    private translate: TranslateService
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Fetch inbox requests
   */
  override fetchRequests(pageNumber: number, delegationId?: number): void {
    this.loadingSubject$.next(true);

    const paginationInfo: PaginationInfo = this.buildPaginationInfo(pageNumber);

    this.requestService.getInbox(paginationInfo, delegationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.updateRequests(response);
          this.loadingSubject$.next(false);
        },
        error: (error) => {
          this.handleError(error);
        }
      });
  }

  /**
   * Open the request when its row is clicked
   */
  onRowClick(row: any): void {
    this.editRequest(row);
  }

  /**
   * Handle action triggered from grid
   */
  onTableAction(event: { action: GridAction; row: any }): void {
    switch (event.action.type) {
      case 'edit':
        this.editRequest(event.row);
        break;
      case 'accept':
        this.acceptRequest(event.row);
        break;
      case 'reject':
        this.rejectRequest(event.row);
        break;
      case 'delegate':
        this.delegateRequest(event.row);
        break;
      case 'forward':
        this.forwardRequest(event.row);
        break;
    }
  }

  /**
   * Edit request in task viewer
   */
  private editRequest(row: any): void {
    if (row?.id && row?.taskId) {
      this.router.navigate(['/requests/task-view', 0, row.id, row.taskId]);
    } else {
      console.warn('Cannot navigate to task viewer: missing request or task ID', row);
    }
  }

  /**
   * View request details in task viewer
   */
  private viewRequest(row: any): void {
    // Navigate to task viewer: /requests/task-view/{pageType}/{requestId}/{taskId}
    if (row?.id && row?.taskId) {
      this.router.navigate(['/requests/task-view', 0, row.id, row.taskId]);
    } else {
      console.warn('Cannot navigate to task viewer: missing request or task ID', row);
    }
  }

  /**
   * Accept request
   */
  private acceptRequest(row: any): void {
    console.log('Accept request:', row);
    // Call API to accept request
  }

  /**
   * Reject request
   */
  private rejectRequest(row: any): void {
    console.log('Reject request:', row);
    // Call API to reject request
  }

  /**
   * Delegate request
   */
  private delegateRequest(row: any): void {
    console.log('Delegate request:', row);
    // Open delegation modal
  }

  /**
   * Forward request
   */
  private forwardRequest(row: any): void {
    console.log('Forward request:', row);
    // Open forward modal
  }
}
